<template>
  <div class="q-mx-auto" style="max-width: 1500px">
    <div class="row justify-around q-pa-sm">
      <div
        v-for="filter in filterOptions"
        :key="filter.value"
        class="text-center q-mx-sm q-px-xs-sm text-body1 text-bold cursor-pointer"
        :class="[
          selectedFilter === filter.value ? 'bg-grey-3 rounded-borders' : '',
          filter.color,
        ]"
        @click="selectedFilter = filter.value"
      >
        <div v-if="filter.value !== 'all'">
          {{ computedData.counts[filter.value] }}
        </div>
        <q-icon v-else name="auto_awesome_mosaic" />
        <q-separator :color="filter.separatorColor" />
        {{ filter.label }}
        <q-tooltip class="bg-primary">
          {{ filter.tooltip }}
        </q-tooltip>
      </div>
    </div>

    <q-table
      flat
      bordered
      v-if="liveMarketData.length"
      :class="
        $q.dark.mode
          ? 'table-sticky-header-column-dark'
          : 'table-sticky-header-column'
      "
      :rows="computedData?.resultData"
      :columns="columnsLiveMarket"
      binary-state-sort
      row-key="symbol"
      :filter="filterLiveMarket"
      :pagination="paginationLiveMarket"
      separator="cell"
      cellspacing-color="white"
      dense
    >
      <template v-slot:top-left>
        <q-input
          class="q-mr-md live-search"
          borderless
          dense
          debounce="300"
          v-model="filterLiveMarket"
          placeholder="Filter Symbols"
        >
          <template v-slot:append>
            <q-icon name="search"></q-icon>
          </template>
        </q-input>
      </template>
      <template v-slot:body-cell-symbol="props">
        <q-td
          :props="props"
          :class="
            props.row.percentage_change < 0
              ? 'bg-red-5 text-white text-bold'
              : props.row.percentage_change > 0
              ? 'bg-green-5 text-white text-bold'
              : 'bg-blue-5 text-white text-bold'
          "
        >
          <q-item
            dense
            clickable
            :to="
              theme === true
                ? {
                    path: '/stock-details',
                    query: { symbol: props.value },
                  }
                : {
                    path: '/stock-profile',
                    query: { symbol: props.value },
                  }
            "
          >
            <div class="col">
              {{ props.value }}
              <q-tooltip>{{
                allStocks.find((stock) => stock.symbol == props.value)?.name
              }}</q-tooltip>
            </div>
          </q-item>
        </q-td>
      </template>
      <template v-slot:body-cell="props">
        <q-td
          :props="props"
          :class="
            props.row.percentage_change < 0
              ? 'bg-red-5 text-white text-bold'
              : props.row.percentage_change > 0
              ? 'bg-green-5 text-white text-bold'
              : 'bg-blue-5 text-white text-bold'
          "
        >
          {{ props.value }}
        </q-td>
      </template>
    </q-table>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from "vue";
import { useStore } from "vuex";
import { LocalStorage, useQuasar } from "quasar";
import useDataV2 from "src/use/datav2";

const props = defineProps({
  allStocks: {
    type: Array,
  },

  theme: {
    type: Boolean,
    default: false,
  },
  selectedSector: {
    type: String,
    default: "NEPSE",
  },
  selectedSectorID: {
    type: Number,
    default: 25,
  },
});

const $q = useQuasar();
const store = useStore();

const { LiveMarketData } = useDataV2();

let intervalId = null;

const isMarketOn = ref(false);
isMarketOn.value = LocalStorage.getItem("isMarketOn");
isMarketOn.value = store.state.market.marketStatus;
const filterLiveMarket = ref("");

const liveMarketData = ref([]);

const selectedFilter = ref("all");
const filterOptions = [
  {
    value: "all",
    label: "All",
    color: "text-blue-grey-7",
    separatorColor: "blue-grey-7",
    tooltip: "Click here to see all stock",
  },
  {
    value: "advanced",
    label: "Advanced",
    color: "text-green-7",
    separatorColor: "green-7",
    tooltip: "Click here to see only Advanced stock",
  },
  {
    value: "unchanged",
    label: "Unchanged",
    color: "text-blue-7",
    separatorColor: "blue-7",
    tooltip: "Click here to see only Unchanged stock",
  },
  {
    value: "declined",
    label: "Declined",
    color: "text-red-7",
    separatorColor: "red-7",
    tooltip: "Click here to see only Declined stock",
  },
];

const loadLiveData = async () => {
  try {
    const res = await LiveMarketData();
    liveMarketData.value = res.data;
  } catch (error) {
    console.error("Error loading live data:", error);
  }
};

const computedData = computed(() => {
  const sectorId = props.selectedSectorID;
  const allStocks = props.allStocks || [];
  const liveData = liveMarketData.value || [];

  const filterConditions = {
    advanced: (item) => item.point_change > 0,
    unchanged: (item) => item.point_change === 0,
    declined: (item) => item.point_change < 0,
    all: () => true, // No filter for 'all'
  };

  const sectorSymbols = allStocks
    ?.filter((i) => i.sector === sectorId)
    .map((stock) => stock.symbol);

  const marketData = liveData.filter((item) => {
    const isSectorMatch =
      sectorId === 25 || sectorSymbols.includes(item.symbol);

    return isSectorMatch;
  });

  const counts = marketData.reduce(
    (acc, curr) => {
      acc.advanced += curr.point_change > 0 ? 1 : 0;
      acc.declined += curr.point_change < 0 ? 1 : 0;
      acc.unchanged += curr.point_change === 0 ? 1 : 0;
      return acc;
    },
    { advanced: 0, declined: 0, unchanged: 0 }
  );

  const resultData = marketData.filter(
    filterConditions[selectedFilter.value] || filterConditions.all
  );

  return {
    counts,
    resultData,
  };
});

if (isMarketOn.value) {
  intervalId = setInterval(() => {
    loadLiveData();
  }, 10000);
}

onUnmounted(() => {
  clearInterval(intervalId);
});

const columnsLiveMarket = [
  {
    name: "symbol",
    label: "Symbol",
    field: "symbol",
    sortable: true,
    align: "center",
  },
  // {
  //   name: "created_at",
  //   label: "Symbol",
  //   field: "created_at",
  //   sortable: true,
  //   align: "left",
  // },

  {
    name: "ltp",
    label: "LTP",
    field: "ltp",
    sortable: true,
    align: "center",
  },
  {
    name: "ltv",
    label: "LTV",
    field: "ltv",
    sortable: true,
    align: "center",
  },

  {
    name: "change",
    label: "Pt.Chng",
    field: "point_change",
    sortable: true,
    sort: (a, b, rowA, rowB) => parseInt(a, 10) - parseInt(b, 10),
    format: (val, row) => {
      return parseFloat(val).toFixed(2);
    },
    align: "center",
  },
  {
    name: "percentage_change",
    label: "% Chng",
    field: "percentage_change",
    sortable: true,
    sort: (a, b, rowA, rowB) => parseInt(a, 10) - parseInt(b, 10),
    format: (val, row) => {
      return parseFloat(val).toFixed(2);
    },
    align: "center",
  },
  {
    name: "open",
    label: "Open",
    field: "open",
    sortable: true,
    align: "center",
  },
  {
    name: "high",
    label: "High",
    field: "high",
    sortable: true,
    align: "center",
  },
  {
    name: "low",
    label: "Low",
    field: "low",
    sortable: true,
    align: "center",
  },
  {
    name: "volume",
    label: "Vol",
    field: "volume",
    sortable: true,
    align: "center",
  },
  // {
  //   name: "turnover",
  //   label: "Turnover",
  //   field: "amount",
  //   sortable: true,
  //   format: (val, row) => {
  //     return amountConversion(row.amount);
  //   },
  //   align: "center",
  // },
];

const paginationLiveMarket = {
  sortBy: "created_at",
  rowsPerPage: 0,
  descending: true,
};

onMounted(() => {
  loadLiveData();
});
</script>

<style lang="scss">
.my-sticky-header-table {
  /* height or max-height is important */
  height: 73vh;

  .q-table__top,
  .q-table__bottom,
  thead tr:first-child th {
    /* bg color is important for th; just specify one */
    background-color: $grey-1;
  }

  thead tr th {
    position: sticky;
    z-index: 1;
  }

  thead tr:first-child th {
    top: 0;
  }

  /* this is when the loading indicator appears */
  &.q-table--loading thead tr:last-child th {
    /* height of all previous header rows */
    top: 48px;
  }

  /* prevent scrolling behind sticky top row on focus */
  tbody {
    /* height of all previous header rows */
    scroll-margin-top: 4px;
  }
}
</style>
